import { GreenCandleClient, type StoredTokens, type TokenStore } from '@greencandle/api-client'

const STORAGE_KEY = 'greencandle.tokens'

/**
 * Browser token storage.
 *
 * D-13 records the trade being made here: with no server of our own in front of the API, the
 * access token has to be readable by JavaScript, so cross-site scripting could take it. It is
 * accepted for a single-user application whose API is token-authenticated anyway, and it is the
 * thing to revisit first if this ever serves more than one person.
 *
 * Every access is guarded: Safari in private mode throws on `localStorage` rather than returning
 * null, and a session that cannot be remembered should still be usable for as long as the tab
 * is open.
 */
const browserTokens: TokenStore = {
  read() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY)
      return raw ? (JSON.parse(raw) as StoredTokens) : null
    } catch {
      return null
    }
  },
  write(tokens) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens))
    } catch {
      // Not fatal: the client keeps working from memory until the tab closes.
    }
  },
  clear() {
    try {
      window.localStorage.removeItem(STORAGE_KEY)
    } catch {
      // Nothing to do - there was nothing stored to begin with.
    }
  },
}

/** Listeners for "the session ended", so the router can react without the client importing it. */
const signOutListeners = new Set<() => void>()

export function onSignedOut(listener: () => void): () => void {
  signOutListeners.add(listener)
  return () => signOutListeners.delete(listener)
}

export const api = new GreenCandleClient({
  // Same origin in both dev and production: Vite proxies /api to the backend locally, and Caddy
  // does the same in front of the box. Nothing here needs to know the API's hostname.
  baseUrl: import.meta.env.VITE_API_URL ?? '',
  tokens: browserTokens,
  onSignedOut: () => {
    for (const listener of signOutListeners) {
      listener()
    }
  },
})
